// ===================================================
// useSessionManager.js — 앱의 모든 상태와 동작을 관리하는 훅
//
// '훅(hook)'이란 여러 화면에서 공통으로 쓰이는 데이터·동작을
// 한 곳에 모아놓은 함수입니다.
//
// 이 파일 하나에 '어떤 화면을 보여줄지', '기록을 어떻게
// 저장/삭제할지', '키워드 검색은 어떻게 동작할지' 등
// 앱 전체의 흐름 로직이 담겨 있습니다.
// App.js는 이 훅을 불러다 쓰기만 합니다.
//
// user: 로그인 사용자 객체 (없으면 null/false → 게스트 모드)
//   - 로그인: Supabase DB에서 데이터 읽기/쓰기
//   - 게스트:  로컬스토리지(guestStorage)에서 읽기/쓰기
// ===================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { loadSessions, deleteSession, fetchLifeSummary } from '../storage';
import { loadGuestSessions, deleteGuestSession } from '../utils/guestStorage';
import { logError } from '../utils/errorLogger';

// 히스토리 항목 타입: { screen, selectedSession, keywordsMode, selectedKeyword }

export function useSessionManager(user) {
  // userRef: refreshSessions 함수를 안정적으로 유지하면서
  // 항상 최신 user 값을 참조하기 위한 ref
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── 현재 화면 ──────────────────────────────────────────────
  // 'landing'       : 홈 화면 (텍스트 입력)
  // 'qa'            : AI 질문에 답변하는 화면
  // 'session-view'  : 저장된 기록 보기
  // 'keywords'      : 키워드 모음 페이지
  // 'keyword-search': 키워드로 기록 검색
  // 'search'        : 통합 검색 페이지
  // 'settings'      : 설정 페이지
  // 'chat'          : 나와의 대화 (과거 기록 기반 채팅)
  // 'calendar'      : 기록 캘린더 (월별 기록 현황 + 스트릭)
  // 'admin'         : 마스터 어드민 대시보드 (마스터 계정 전용)
  const [screen, setScreen] = useState('landing');

  // ── 뒤로가기 히스토리 스택 ────────────────────────────────
  // 배열로 관리: 새 화면으로 이동할 때 현재 상태를 쌓고,
  // 뒤로가기 시 꺼내서 복원합니다.
  // ref를 쓰는 이유: 상태 변경 없이 읽기/쓰기가 가능하고, 리렌더를 유발하지 않음
  const screenHistoryRef = useRef([]);
  const [loading, setLoading] = useState(false); // AI 처리 중 여부
  const [error, setError] = useState('');         // 오류 메시지

  // ── 입력 모드 ──────────────────────────────────────────────
  // 'diary' | 'note' | 'planner' | 'decision'
  const [mode, setMode] = useState('diary');

  // ── 현재 작성 중인 세션의 임시 데이터 ─────────────────────
  const [initialText, setInitialText] = useState('');
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [sessionRounds, setSessionRounds] = useState([]);

  // ── 의사결정 모드 전용: 이미 탐색한 사고 축 목록 ────────────
  // 세션이 이어질수록 중복 질문을 방지하기 위해 사용
  // 예: ['decision_criteria', 'fear', 'practical_constraint', ...]
  const [decisionAxes, setDecisionAxes] = useState([]);

  // ── 저장된 전체 기록 ────────────────────────────────────────
  // 로그인: Supabase DB / 게스트: 로컬스토리지에서 비동기로 불러옴
  const [sessions, setSessions] = useState({});
  // 세션 첫 로딩이 완료됐는지 여부
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  // 기록 로딩 실패 여부 — Supabase 조회 오류 시 true
  const [sessionsError, setSessionsError] = useState(false);

  // ── B1 유저 삶의 흐름 요약 캐시 ────────────────────────────────
  // 로그인 시 한 번만 DB에서 가져와 상태로 유지합니다.
  // 새 기록 저장 후 handleEnd에서 갱신됩니다.
  const [lifeSummary, setLifeSummary] = useState(null);

  // ── 사이드바에서 선택한 세션 { date, idx } ─────────────────
  const [selectedSession, setSelectedSession] = useState(null);

  // ── 키워드 관련 상태 ────────────────────────────────────────
  const [keywordsMode, setKeywordsMode] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  // ── 모바일 기록 드로어(서랍) 상태 ────────────────────────────
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const closeMobileDrawer = () => setMobileDrawerOpen(false);
  const toggleMobileDrawer = () => setMobileDrawerOpen(prev => !prev);

  // ── 헬퍼 함수 ──────────────────────────────────────────────

  // 기록을 다시 불러와 화면에 반영
  // userRef를 사용해 함수를 안정적으로 유지: user가 바뀌어도 재생성되지 않음
  // → App.js가 마이그레이션 완료 후 직접 호출해 타이밍 제어
  const refreshSessions = useCallback(async () => {
    try {
      setSessionsError(false);
      const data = userRef.current ? await loadSessions() : loadGuestSessions();
      setSessions(data);
      setSessionsLoaded(true);
    } catch (err) {
      // Supabase 조회 자체가 예외를 던진 경우 (네트워크 단절 등)
      setSessionsError(true);
      setSessionsLoaded(true);
      logError({ action: 'refreshSessions', error: err, screen: 'app' });
    }
  }, []); // 의도적으로 deps 비움 — userRef로 최신값 참조

  // 기록 로드: 처음 + user 상태가 바뀔 때 (로그인/로그아웃 감지)
  // user: null(확인 중) → false(비로그인) 또는 객체(로그인) 순으로 변함
  // userRef는 이 useEffect보다 먼저 실행되므로 항상 최신 user를 참조
  useEffect(() => {
    refreshSessions();
  }, [refreshSessions, user]);

  // 로그인 시 life summary를 한 번만 가져옵니다.
  // 로그아웃 시 null로 초기화합니다.
  useEffect(() => {
    if (!user) { setLifeSummary(null); return; }
    fetchLifeSummary().then(summary => setLifeSummary(summary)).catch(() => {});
  }, [user]);

  // 새 세션 작성 완료 후 임시 데이터 초기화
  const resetSession = () => {
    setInitialText('');
    setCurrentQuestions([]);
    setSessionRounds([]);
    setDecisionAxes([]);
    setError('');
    setLoading(false);
  };

  // ── 화면 전환 핸들러 ────────────────────────────────────────

  // 현재 화면 상태를 히스토리에 저장하고 새 화면으로 이동
  // stateUpdate: 새 화면에서 적용할 상태 변경 (선택적)
  const navigateTo = (newScreen, stateUpdate = {}) => {
    // 현재 상태를 히스토리 스택에 push
    screenHistoryRef.current.push({
      screen,
      selectedSession,
      keywordsMode,
      selectedKeyword,
    });
    // 새 상태 적용
    setScreen(newScreen);
    if ('selectedSession' in stateUpdate) setSelectedSession(stateUpdate.selectedSession);
    if ('keywordsMode' in stateUpdate) setKeywordsMode(stateUpdate.keywordsMode);
    if ('selectedKeyword' in stateUpdate) setSelectedKeyword(stateUpdate.selectedKeyword);
    setMobileDrawerOpen(false);
  };

  // 히스토리 스택에서 이전 상태를 꺼내 뒤로 이동
  // 히스토리가 비어있으면 랜딩 화면으로
  const goBack = () => {
    const prev = screenHistoryRef.current.pop();
    if (!prev) {
      setScreen('landing');
      setSelectedSession(null);
      setMobileDrawerOpen(false);
      return;
    }
    setScreen(prev.screen);
    setSelectedSession(prev.selectedSession);
    setKeywordsMode(prev.keywordsMode);
    setSelectedKeyword(prev.selectedKeyword);
    setMobileDrawerOpen(false);
  };

  // 사이드바에서 특정 세션 클릭 → 세션 보기 화면으로
  const handleSelectSession = (date, idx) => {
    navigateTo('session-view', { selectedSession: { date, idx } });
  };

  // '홈으로' 버튼 → 히스토리 초기화 후 랜딩 화면으로
  const handleHome = () => {
    screenHistoryRef.current = [];
    setSelectedSession(null);
    setScreen('landing');
    setMobileDrawerOpen(false);
  };

  // 현재 보고 있는 세션 삭제 후 홈으로
  // 로그인: Supabase DB 삭제 / 게스트: 로컬스토리지 삭제
  const handleDeleteSession = async () => {
    const session = sessions[selectedSession.date]?.[selectedSession.idx];
    if (session?.id) {
      if (userRef.current) await deleteSession(session.id);
      else deleteGuestSession(session.id);
    }
    await refreshSessions();
    screenHistoryRef.current = [];
    setSelectedSession(null);
    setScreen('landing');
    setMobileDrawerOpen(false);
  };

  // 키워드 페이지로 이동
  const handleSelectKeywords = (m) => {
    navigateTo('keywords', { keywordsMode: m, selectedSession: null });
  };

  // 키워드 태그 클릭 → 키워드 검색 결과 화면으로
  const handleKeywordClick = (keyword) => {
    navigateTo('keyword-search', { selectedKeyword: keyword });
  };

  // 검색 페이지 열기
  const handleOpenSearch = () => {
    navigateTo('search', { selectedSession: null });
  };

  // 설정 페이지 열기
  const handleOpenSettings = () => {
    navigateTo('settings', { selectedSession: null });
  };

  // '나와의 대화' 채팅 화면 열기
  const handleOpenChat = () => {
    navigateTo('chat', { selectedSession: null });
  };

  // '기록 캘린더' 화면 열기
  const handleOpenCalendar = () => {
    navigateTo('calendar', { selectedSession: null });
  };

  const handleOpenAdmin = () => {
    navigateTo('admin', { selectedSession: null });
  };

  // '앱 알림 관리' 화면 열기
  const handleOpenPushSettings = () => {
    navigateTo('push-settings', { selectedSession: null });
  };

  // 키워드 검색에서 뒤로가기 → 히스토리 스택 사용
  const handleBackFromKeywordSearch = () => {
    goBack();
  };

  return {
    // 상태 값
    screen, loading, error, mode,
    initialText, currentQuestions, questionsLoading, sessionRounds,
    decisionAxes,
    sessions, sessionsLoaded, sessionsError, selectedSession,
    keywordsMode, selectedKeyword,
    mobileDrawerOpen,
    lifeSummary, setLifeSummary,
    // 상태 변경 함수
    setMode, setScreen, setLoading, setError,
    setInitialText, setCurrentQuestions, setQuestionsLoading, setSessionRounds,
    setDecisionAxes,
    // 헬퍼
    refreshSessions, resetSession,
    closeMobileDrawer, toggleMobileDrawer,
    // 화면 전환 핸들러
    goBack,
    handleSelectSession, handleHome, handleDeleteSession,
    handleSelectKeywords, handleKeywordClick, handleBackFromKeywordSearch,
    handleOpenSearch, handleOpenSettings, handleOpenChat, handleOpenCalendar, handleOpenAdmin, handleOpenPushSettings,
  };
}
